// @file pinia store for accessibility settings
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { isRegistered } from '@@/bits/user_model'
import { AccessibilitySettingPreference, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useScreenReaderNotificationsStore } from '@@/pinia/screen_reader_notifications'
import { UserApi } from '@@/surface/api/user'
import type { User as UserSnackcase, UserCamelCase as User } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum UserInfoStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

enum AccessibilitySetting {
  KeyboardShortcuts = 'keyboardShortcuts',
  AppMessagesAutoDismiss = 'autoDismissAppMessages',
  HighContrastMode = 'highContrastMode',
  ReducedMotion = 'reducedMotion',
}

type UserAccessibilitySettings = Pick<
  User,
  'isKeyboardShortcutsEnabled' | 'isAppMessagesAutoDismissed' | 'highContrastModePreference' | 'reducedMotionPreference'
>

interface AwaitingSaveStates {
  isKeyboardShortcuts: boolean
  isAppMessages: boolean
  highContrastMode: boolean
  reducedMotion: boolean
}

interface SettingParams {
  eventName: string
  successMessage: string
  errorMessage: string
  errorSource: string
}

interface BooleanSettingParams extends SettingParams {
  key: 'isKeyboardShortcutsEnabled' | 'isAppMessagesAutoDismissed'
}

interface PreferenceSettingParams extends SettingParams {
  key: 'highContrastModePreference' | 'reducedMotionPreference'
  value: AccessibilitySettingPreference
}

interface LocalAccessibilitySettings {
  isKeyboardShortcutsEnabled: boolean
  isAppMessagesAutoDismissed: boolean
  highContrastModePreference: AccessibilitySettingPreference
  reducedMotionPreference: AccessibilitySettingPreference
}

const DEFAULT_USER_ACCESSIBILITY_SETTINGS: Required<UserAccessibilitySettings> = {
  isKeyboardShortcutsEnabled: true,
  isAppMessagesAutoDismissed: true,
  highContrastModePreference: AccessibilitySettingPreference.System,
  reducedMotionPreference: AccessibilitySettingPreference.System,
}

function resolveLocalStorage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const useAccessibilitySettingsStore = defineStore('accessibilitySettings', () => {
  const screenReaderNotificationStore = useScreenReaderNotificationsStore()

  const userSettings = ref<UserAccessibilitySettings | null>(null)
  const isKeyboardShortcutsEnabled = ref(DEFAULT_USER_ACCESSIBILITY_SETTINGS.isKeyboardShortcutsEnabled)
  const isAppMessagesAutoDismissed = ref(DEFAULT_USER_ACCESSIBILITY_SETTINGS.isAppMessagesAutoDismissed)
  const highContrastModePreference = ref<AccessibilitySettingPreference>(
    DEFAULT_USER_ACCESSIBILITY_SETTINGS.highContrastModePreference,
  )
  const reducedMotionPreference = ref<AccessibilitySettingPreference>(
    DEFAULT_USER_ACCESSIBILITY_SETTINGS.reducedMotionPreference,
  )

  const userInfoStatus = ref(UserInfoStatus.Loading)
  const isLoadingUserInfo = computed(() => userInfoStatus.value === UserInfoStatus.Loading)

  const isAwaitingSaveIsKeyboardShortcutsEnabledResponse = computed(() => isAwaitingSaveMap.value.isKeyboardShortcuts)
  const isAwaitingSaveIsAppMessagesAutoDismissedResponse = computed(() => isAwaitingSaveMap.value.isAppMessages)
  const isAwaitingSaveHighContrastModePreferenceResponse = computed(() => isAwaitingSaveMap.value.highContrastMode)
  const isAwaitingSaveReducedMotionPreferenceResponse = computed(() => isAwaitingSaveMap.value.reducedMotion)

  const xAccessibilitySettingsModal = ref(false)

  const isAwaitingSaveMap = ref<AwaitingSaveStates>({
    isKeyboardShortcuts: false,
    isAppMessages: false,
    highContrastMode: false,
    reducedMotion: false,
  })

  const stateUpdatesShouldBeLocallySaved = ref(false)
  let activeRegisteredUserId: User['id'] | null = null
  let userInfoFetchGeneration = 0
  let saveOperationId = 0
  const activeSaveOperationByKey: Partial<Record<keyof AwaitingSaveStates, number>> = {}

  const clearAwaitingSaveStates = (): void => {
    isAwaitingSaveMap.value = {
      isKeyboardShortcuts: false,
      isAppMessages: false,
      highContrastMode: false,
      reducedMotion: false,
    }
    for (const key of Object.keys(activeSaveOperationByKey) as Array<keyof AwaitingSaveStates>) {
      delete activeSaveOperationByKey[key]
    }
  }

  const localSettings = useStorage<LocalAccessibilitySettings>(
    'accessibility_settings',
    DEFAULT_USER_ACCESSIBILITY_SETTINGS,
    resolveLocalStorage(),
  )

  async function updateBooleanSetting({
    key,
    eventName,
    successMessage,
    errorMessage,
    errorSource,
  }: BooleanSettingParams): Promise<void> {
    const stateRef = key === 'isKeyboardShortcutsEnabled' ? isKeyboardShortcutsEnabled : isAppMessagesAutoDismissed
    const loadingKey = key === 'isKeyboardShortcutsEnabled' ? 'isKeyboardShortcuts' : 'isAppMessages'
    const operationGeneration = userInfoFetchGeneration

    stateRef.value = !stateRef.value

    if (stateUpdatesShouldBeLocallySaved.value) {
      localSettings.value[key] = stateRef.value
      if (userSettings.value != null) {
        userSettings.value = {
          ...userSettings.value,
          [key]: stateRef.value,
        }
      }
      screenReaderNotificationStore.addScreenReaderMessage(successMessage)
      return
    }

    const saveId = ++saveOperationId
    try {
      activeSaveOperationByKey[loadingKey] = saveId
      isAwaitingSaveMap.value[loadingKey] = true
      trackEvent(eventName, 'Toggled setting', stateRef.value)

      await UserApi.updateSettingsUserInfo({
        body: asciiSafeStringify({
          data: {
            attributes: { [key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]: stateRef.value },
          },
        }),
      })
      if (operationGeneration !== userInfoFetchGeneration) return

      userSettings.value = {
        ...userSettings.value,
        [key]: stateRef.value,
      }
      screenReaderNotificationStore.addScreenReaderMessage(successMessage)
    } catch (e) {
      if (operationGeneration !== userInfoFetchGeneration) return

      captureFetchException(e, { source: errorSource })
      void useGlobalSnackbarStore().setSnackbar({
        message: errorMessage,
        notificationType: SnackbarNotificationType.error,
      })
      stateRef.value = !stateRef.value
    } finally {
      // Clear when this save still owns the flag — including after a newer
      // userInfoFetchGeneration — so a stale PATCH cannot leave the toggle stuck.
      // A newer save for the same key replaces the owner and is left alone.
      if (activeSaveOperationByKey[loadingKey] === saveId) {
        isAwaitingSaveMap.value[loadingKey] = false
        delete activeSaveOperationByKey[loadingKey]
      }
    }
  }

  async function updatePreferenceSetting({
    key,
    value,
    eventName,
    successMessage,
    errorMessage,
    errorSource,
  }: PreferenceSettingParams): Promise<void> {
    const stateRef = key === 'highContrastModePreference' ? highContrastModePreference : reducedMotionPreference
    const loadingKey = key === 'highContrastModePreference' ? 'highContrastMode' : 'reducedMotion'
    const operationGeneration = userInfoFetchGeneration
    const previousValue = stateRef.value
    stateRef.value = value

    if (stateUpdatesShouldBeLocallySaved.value) {
      localSettings.value[key] = value
      if (userSettings.value != null) {
        userSettings.value = {
          ...userSettings.value,
          [key]: value,
        }
      }
      screenReaderNotificationStore.addScreenReaderMessage(successMessage)
      return
    }

    const saveId = ++saveOperationId
    try {
      activeSaveOperationByKey[loadingKey] = saveId
      isAwaitingSaveMap.value[loadingKey] = true
      trackEvent(eventName, 'Updated setting', value)

      await UserApi.updateSettingsUserInfo({
        body: asciiSafeStringify({
          data: {
            attributes: { [key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)]: value },
          },
        }),
      })
      if (operationGeneration !== userInfoFetchGeneration) return

      userSettings.value = {
        ...userSettings.value,
        [key]: value,
      }
      screenReaderNotificationStore.addScreenReaderMessage(successMessage)
    } catch (e) {
      if (operationGeneration !== userInfoFetchGeneration) return

      captureFetchException(e, { source: errorSource })
      void useGlobalSnackbarStore().setSnackbar({
        message: errorMessage,
        notificationType: SnackbarNotificationType.error,
      })
      stateRef.value = previousValue
    } finally {
      // Clear when this save still owns the flag — including after a newer
      // userInfoFetchGeneration — so a stale PATCH cannot leave the toggle stuck.
      // A newer save for the same key replaces the owner and is left alone.
      if (activeSaveOperationByKey[loadingKey] === saveId) {
        isAwaitingSaveMap.value[loadingKey] = false
        delete activeSaveOperationByKey[loadingKey]
      }
    }
  }

  async function toggleIsKeyboardShortcutsEnabled(): Promise<void> {
    return await updateBooleanSetting({
      key: 'isKeyboardShortcutsEnabled',
      eventName: 'Keyboard shortcuts',
      successMessage: __('Your preference for keyboard shortcuts has been updated.'),
      errorMessage: __('Error updating keyboard shortcuts'),
      errorSource: 'AccessibilitySettingsStoreSaveIsKeyboardShortcutsEnabled',
    })
  }

  async function toggleIsAppMessagesAutoDismissed(): Promise<void> {
    return await updateBooleanSetting({
      key: 'isAppMessagesAutoDismissed',
      eventName: 'Automatically dismiss app messages',
      successMessage: __('Your preference for automatically dismissing app messages has been updated.'),
      errorMessage: __('Error updating setting for automatically dismissing app messages'),
      errorSource: 'AccessibilitySettingsStoreSaveIsAppMessagesAutoDismissed',
    })
  }

  async function updateHighContrastModePreference(value: AccessibilitySettingPreference): Promise<void> {
    return await updatePreferenceSetting({
      key: 'highContrastModePreference',
      value,
      eventName: 'High contrast mode',
      successMessage: __('Your preference for high contrast mode has been updated.'),
      errorMessage: __('Error updating high contrast mode setting'),
      errorSource: 'AccessibilitySettingsStoreSaveHighContrastModePreference',
    })
  }

  async function updateReducedMotionPreference(value: AccessibilitySettingPreference): Promise<void> {
    return await updatePreferenceSetting({
      key: 'reducedMotionPreference',
      value,
      eventName: 'Reduced motion',
      successMessage: __('Your preference for reduced motion has been updated.'),
      errorMessage: __('Error updating reduced motion setting'),
      errorSource: 'AccessibilitySettingsStoreSaveReducedMotionPreference',
    })
  }

  function initializeState({
    preserveUserInfoStatus = false,
  }: {
    preserveUserInfoStatus?: boolean
  } = {}): void {
    if (userSettings.value != null) {
      isKeyboardShortcutsEnabled.value = userSettings.value.isKeyboardShortcutsEnabled ?? true
      isAppMessagesAutoDismissed.value = userSettings.value.isAppMessagesAutoDismissed ?? true
      highContrastModePreference.value =
        userSettings.value.highContrastModePreference ?? AccessibilitySettingPreference.System
      reducedMotionPreference.value =
        userSettings.value.reducedMotionPreference ?? AccessibilitySettingPreference.System
    } else {
      stateUpdatesShouldBeLocallySaved.value = true
      isKeyboardShortcutsEnabled.value = localSettings.value.isKeyboardShortcutsEnabled
      isAppMessagesAutoDismissed.value = localSettings.value.isAppMessagesAutoDismissed
      highContrastModePreference.value = localSettings.value.highContrastModePreference
      reducedMotionPreference.value = localSettings.value.reducedMotionPreference
      if (!preserveUserInfoStatus) {
        userInfoStatus.value = UserInfoStatus.Completed
      }
    }
  }

  async function fetchUserInfo(fetchGeneration = userInfoFetchGeneration): Promise<void> {
    try {
      userInfoStatus.value = UserInfoStatus.Loading
      const response = await UserApi.fetchSettingsUserInfo()
      if (fetchGeneration !== userInfoFetchGeneration) return

      const user = (response.data as JsonAPIResource<User>).attributes
      userSettings.value = user
      userInfoStatus.value = UserInfoStatus.Completed
    } catch (e) {
      if (fetchGeneration !== userInfoFetchGeneration) return

      if (e instanceof Error && e.message.includes('401')) {
        userInfoStatus.value = UserInfoStatus.Completed
      } else {
        captureFetchException(e, { source: 'AccessibilitySettingsStoreFetchUserInfo' })
        userInfoStatus.value = UserInfoStatus.Errored
      }
    } finally {
      if (fetchGeneration === userInfoFetchGeneration) {
        initializeState({ preserveUserInfoStatus: true })
      }
    }
  }

  async function syncLocalSettingsToServer(): Promise<void> {
    const operationGeneration = userInfoFetchGeneration
    try {
      if (userSettings.value == null) {
        await fetchUserInfo()
      }
      if (operationGeneration !== userInfoFetchGeneration) return

      // Stay in local-save mode if server state was never obtained, so toggles
      // keep persisting to localStorage instead of being lost on refresh.
      if (userSettings.value == null) {
        return
      }

      const migrated: Record<string, boolean | AccessibilitySettingPreference> = {}
      for (const key of Object.keys(DEFAULT_USER_ACCESSIBILITY_SETTINGS) as (keyof UserAccessibilitySettings)[]) {
        const defaultValue = DEFAULT_USER_ACCESSIBILITY_SETTINGS[key]
        const serverValue = userSettings.value[key]
        const localValue = localSettings.value[key]
        if ((serverValue == null || serverValue === defaultValue) && localValue !== defaultValue) {
          migrated[key] = localValue
        }
      }

      if (Object.keys(migrated).length === 0) {
        // Set flag to use server settings going forward
        stateUpdatesShouldBeLocallySaved.value = false
        return
      }

      const attributes: Record<string, boolean | AccessibilitySettingPreference> = {}
      for (const key of Object.keys(migrated)) {
        attributes[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] = migrated[key]
      }

      await UserApi.updateSettingsUserInfo({
        body: asciiSafeStringify({ data: { attributes } }),
      })
      if (operationGeneration !== userInfoFetchGeneration) return

      userSettings.value = {
        ...userSettings.value,
        ...(migrated as UserAccessibilitySettings),
      }

      // Set flag to use server settings going forward
      stateUpdatesShouldBeLocallySaved.value = false
    } catch (e) {
      if (operationGeneration !== userInfoFetchGeneration) return
      captureFetchException(e, { source: 'AccessibilitySettingsStoreSyncLocalToServer' })
    }
  }

  const init = (user?: User | UserSnackcase | undefined): void => {
    if (user == null || !isRegistered(user)) {
      userInfoFetchGeneration++
      activeRegisteredUserId = null
      clearAwaitingSaveStates()
      userSettings.value = null
      stateUpdatesShouldBeLocallySaved.value = true
      initializeState()
      return
    }

    // Same user already associated — including after a failed fetch. Retrying
    // on Errored from every caller (per-subscriber registration) storms the
    // backend; recovery is via an explicit fetchUserInfo or a user change.
    if (activeRegisteredUserId === user.id) {
      return
    }

    // Preserve a page-local override that was established after settings had
    // already loaded, while still associating it with this user for logout.
    if (activeRegisteredUserId == null && stateUpdatesShouldBeLocallySaved.value && userSettings.value != null) {
      activeRegisteredUserId = user.id
      return
    }

    if (activeRegisteredUserId != null && activeRegisteredUserId !== user.id) {
      // Account-scoped settings must not remain visible while the next user's
      // request is in flight or if that request fails.
      userSettings.value = null
      initializeState()
    }

    // Invalidate in-flight saves whenever we start a new user-info generation,
    // including first fetch after anonymous and account switches.
    clearAwaitingSaveStates()
    activeRegisteredUserId = user.id
    stateUpdatesShouldBeLocallySaved.value = false
    const fetchGeneration = ++userInfoFetchGeneration
    void fetchUserInfo(fetchGeneration)
  }

  const openAccessibilitySettingsModal = (): void => {
    xAccessibilitySettingsModal.value = true
  }

  const closeAccessibilitySettingsModal = (): void => {
    xAccessibilitySettingsModal.value = false
  }

  const setStateUpdatesToBeLocallySaved = (value: boolean): void => {
    stateUpdatesShouldBeLocallySaved.value = value
  }

  return {
    // Setting states
    userSettings,
    isLoadingUserInfo,
    isKeyboardShortcutsEnabled,
    isAppMessagesAutoDismissed,
    highContrastModePreference,
    reducedMotionPreference,

    // UI state
    xAccessibilitySettingsModal,

    // Loading states
    isAwaitingSaveIsKeyboardShortcutsEnabledResponse,
    isAwaitingSaveIsAppMessagesAutoDismissedResponse,
    isAwaitingSaveHighContrastModePreferenceResponse,
    isAwaitingSaveReducedMotionPreferenceResponse,

    // Actions
    init,
    fetchUserInfo,
    toggleIsKeyboardShortcutsEnabled,
    toggleIsAppMessagesAutoDismissed,
    updateHighContrastModePreference,
    updateReducedMotionPreference,
    openAccessibilitySettingsModal,
    closeAccessibilitySettingsModal,
    setStateUpdatesToBeLocallySaved,
    syncLocalSettingsToServer,
  }
})
export { AccessibilitySetting, useAccessibilitySettingsStore }
export type { UserAccessibilitySettings }
